import React, { useState } from "react";
import { Box, HStack } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { Dropdown } from "components/ds";

interface CampaignFiltersProps {
  onFilterChange?: (filters: any) => void;
}

const SORT_OPTIONS = [
  { value: "inserted_at", label: "Created Date" },
  { value: "due_date", label: "Due Date" },
];

/**
 * CampaignFilters — the filter bar under the Open/Completed tabs (PLO-4803).
 * Three small split-pill selectors: Status (the tabs own status, so this is a
 * static "All"), Sort by (the one wired control — persists to the URL), and
 * Group by (static "None"). Left-aligned, gap sm.
 */
const CampaignFilters: React.FC<CampaignFiltersProps> = ({ onFilterChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOpen, setSortOpen] = useState(false);

  // Default sort: newest first (Created Date desc) — keep in sync with CampaignList.
  const currentSort = searchParams.get("sort") || "inserted_at";
  const currentSortLabel =
    SORT_OPTIONS.find((o) => o.value === currentSort)?.label ?? "Created Date";

  const handleSortChange = (sortId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("sort", sortId);
    setSearchParams(newParams);
    onFilterChange?.({ sort_by: sortId, sort_direction: "desc" });
    setSortOpen(false);
  };

  return (
    <HStack spacing="sm" align="center" flexWrap="wrap">
      <Dropdown label="Status" value="All" size="sm" />

      <Box position="relative">
        <Dropdown
          label="Sort by"
          value={currentSortLabel}
          size="sm"
          open={sortOpen}
          onClick={() => setSortOpen((o) => !o)}
        />
        {sortOpen && (
          <>
            <Box
              position="fixed"
              inset={0}
              zIndex={9}
              onClick={() => setSortOpen(false)}
            />
            <Box
              position="absolute"
              top="calc(100% + 4px)"
              left={0}
              minW="164px"
              zIndex={10}
              bg="bg.container"
              borderRadius="component.lg"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="border.default"
              boxShadow="overlay"
              py="2xs"
              overflow="hidden"
            >
              {SORT_OPTIONS.map((o) => (
                <Box
                  key={o.value}
                  as="button"
                  type="button"
                  display="block"
                  width="full"
                  textAlign="left"
                  px="md"
                  py="sm"
                  fontSize="smd"
                  fontWeight={o.value === currentSort ? 600 : 500}
                  color={
                    o.value === currentSort
                      ? "text.body.azurite"
                      : "text.body.primary"
                  }
                  bg="transparent"
                  _hover={{ bg: "bg.hover" }}
                  onClick={() => handleSortChange(o.value)}
                >
                  {o.label}
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      <Dropdown label="Group by" value="None" size="sm" />
    </HStack>
  );
};

export default CampaignFilters;
